import { useMutation } from 'react-query';
import { useApiPost, useApiPut } from 'src/api';
import { useBuildVars } from 'src/config';
import { useEndUsersHeaders } from './end-user-headers';
import { DeatilsResponse } from './deal-details';
import {
  CreateNewEndUserFields,
  useCreateNewEndUserFormFields,
  CreateNewEndUserFormKeys,
  FormError,
} from './create-new-end-user-form';
import { Address, getERPType } from './exceptionDeal';
import { getStateAbbr } from './getStateAbbr';

export type ContactInfo = {
  contactName: string;
  contactType: string;
  email: string;
  phoneNumber: string;
};
export type CreateNewEndUserRequest = {
  address: {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    city: string;
    countryCode: string;
    postalCode: string;
    state: string;
  };
  contactInfo?: Array<ContactInfo>;
  customAccountId: string;
  endUserName: string;
  endUserNoHold: boolean;
  erpType?: string;
  marketSegmentType: string;
  overrideDNBRecommendation: boolean;
  overrideRecommendation: boolean;
};

export type CreateNewEndUserResponse = {
  addressId: number;
  contactId: number;
  directshipeEnduser: {
    directshipheaderenduseraddressid: number;
    directshipheaderenduserid: number;
  };
  endUserId: number;
  erpEndUserIdReferences: {
    acopEndUserId: number;
    erpAddressId: number;
    erpContactId: number;
    ingramEndUserId: number;
    resellerEndUserId: number;
    vendorEndUserId: number;
  };
  status: string;
};

export function useCreateNewEndUser(
  request: CreateNewEndUserRequest,
  customerNumber: string,
  countryCode: string
) {
  const buildVars = useBuildVars();
  const endUserHeaders = useEndUsersHeaders(customerNumber, countryCode);
  const apiPost = useApiPost<CreateNewEndUserResponse>({
    host: buildVars.EU_BASE,
  });
  return useMutation(() =>
    apiPost(
      `/system.api.enduser/v2/enduser`,
      request,
      undefined,
      endUserHeaders
    )
  );
}

export const getFieldValue = (
  key: keyof CreateNewEndUserFields,
  dataPopulateType: string,
  details?: DeatilsResponse
) => {
  if (dataPopulateType === 'From bid') {
    switch (key) {
      case 'endUserName':
        return details?.end_user_name;
      case 'contactName':
        return details?.end_user_contactname;
      case 'email':
        return details?.end_user_email;
      case 'addressLine1':
        return details?.end_user_address1;
      case 'addressLine2':
        return details?.end_user_address2;
      case 'city':
        return details?.end_user_city;
      case 'state':
        return getStateAbbr(details?.end_user_state);
      case 'postalCode':
        return details?.end_user_zipcode;
      case 'countryCode':
        return details?.end_user_country;
      default:
        return '';
    }
  } else if (dataPopulateType === 'Default address') {
    switch (key) {
      case 'addressLine1':
        return 'DEFAULT - ENTER ADDRESS INFO';
      case 'postalCode':
        return '111111111';
      case 'state':
        return 'DE';
      case 'city':
        return 'DEFAULT';
      default:
        return '';
    }
  }
};

export const useCreateNewEndUserFieldMapper = (
  bussinessType: string | null,
  countryCode: string
) => {
  const CreateNewEndUserFormFields =
    useCreateNewEndUserFormFields(bussinessType);
  return (dataPopulateType: string, details?: DeatilsResponse) => {
    const formData: Partial<Record<keyof CreateNewEndUserFields, string>> = {};
    Object.keys(CreateNewEndUserFormFields).forEach((key) => {
      formData[key as keyof CreateNewEndUserFields] = getFieldValue(
        key as keyof CreateNewEndUserFields,
        dataPopulateType,
        details
      );
    });
    return formData;
  };
};

export const createNewEndUserRequestCreator = (
  formData: Record<keyof CreateNewEndUserFields, string>,
  businessType: string | null,
  isHybridDeal: boolean
): CreateNewEndUserRequest => {
  const contactInfoData: Array<ContactInfo> = [];
  if (formData?.contactName) {
    contactInfoData.push({
      contactName: formData.contactName,
      contactType: 'A',
      email: formData.email,
      phoneNumber: formData.phoneNumber,
    });
  }

  if (formData?.technicalContactName) {
    contactInfoData.push({
      contactName: formData.technicalContactName,
      contactType: 'T',
      email: formData.technicalEmail,
      phoneNumber: formData.technicalPhoneNumber,
    });
  }

  if (formData?.billingContactName) {
    contactInfoData.push({
      contactName: formData.billingContactName,
      contactType: 'B',
      email: formData.billingEmail,
      phoneNumber: formData.billingPhoneNumber,
    });
  }
  return {
    address: {
      addressLine1: formData?.addressLine1,
      addressLine2: formData?.addressLine2,
      addressLine3: formData.addressLine3,
      city: formData?.city,
      countryCode: formData.countryCode
        ? formData.countryCode.toUpperCase()
        : '',
      postalCode: formData?.postalCode,
      state: formData.state ? formData.state.toUpperCase() : '',
    },
    contactInfo: contactInfoData,
    customAccountId: formData?.customAccountId,
    endUserName: formData?.endUserName,
    endUserNoHold: true,
    erpType: getERPType(businessType === 'Subscription', isHybridDeal),
    marketSegmentType: formData?.marketSegmentType,
    overrideDNBRecommendation: true,
    overrideRecommendation: true,
  };
};

export const extractEndUserServerErrors = (
  errors: { field: CreateNewEndUserFormKeys; message: string }[]
) => {
  const formError: FormError = {} as FormError;
  for (const e of errors) {
    formError[e.field.split('.').reverse()[0] as CreateNewEndUserFormKeys] =
      e.message;
  }
  return formError;
};

export interface EndUserInfo {
  address: Address[];
  endUserId: number;
  endUserName: string;
}

export interface NewAddress {
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  city: string;
  countryCode: string;
  erpType: string;
  postalCode: string;
  state: string;
}
export interface AddEndUserAddressRequest {
  address: NewAddress;
  endUserId: number;
  endUserNoHold: boolean;
  overrideRecommendation: boolean;
}
export interface EndUserIdRef {
  acopEndUserId: number;
  erpAddressId: number;
  erpContactId: number;
  ingramEndUserId: null;
  resellerEndUserId: number;
  vendorEndUserId: null;
}
export interface AddEndUserAddressResponse {
  addressId: number;
  contactId: number;
  endUserId: number;
  erpEndUserIdReferences: EndUserIdRef;
  status: string;
}

export function useAddEndUserAddress(
  customerNumber: string,
  countryCode: string
) {
  const buildVars = useBuildVars();
  const endUserHeaders = useEndUsersHeaders(customerNumber, countryCode);
  const apiPut = useApiPut<AddEndUserAddressResponse>(buildVars.EU_BASE);
  return useMutation((request: AddEndUserAddressRequest) =>
    apiPut(
      `/system.api.enduser/v2/enduser/address`,
      request,
      undefined,
      endUserHeaders
    )
  );
}
